import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MKT = "https://images.unsplash.com/photo-1604005050685-97d0e19a1842?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export function About() {
  return (
    <div>
      <section className="relative h-[50vh] min-h-[360px] overflow-hidden">
        <img src={MKT} alt="GULIT marketplace" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-sm">Our Story</p>
          <h1 className="font-display text-5xl md:text-6xl text-white mt-3">About GULIT</h1>
        </div>
      </section>
      <div className="mx-auto max-w-3xl px-6 py-16 space-y-6 text-lg leading-relaxed text-muted-foreground">
        <p className="font-accent text-2xl text-foreground">"Gulit" is the Ethiopian word for the neighborhood marketplace — a place where people gather to buy, sell, and build trust.</p>
        <p>Our mission is to bring that warm, trusted marketplace experience to customers around the world. Every product we curate carries a story of craft, care, and community.</p>
        <p>From apparel and home décor to Christian products, gifts and lifestyle goods, GULIT blends timeless elegance with the soul of the marketplace that inspired us. We believe shopping should feel personal, joyful, and rooted in trust.</p>
        <div className="grid sm:grid-cols-3 gap-6 pt-6">
          {[["10k+", "Happy customers"], ["50+", "Countries shipped"], ["4.8★", "Average rating"]].map(([n, l]) => (
            <div key={l} className="text-center border border-border p-8"><p className="font-display text-4xl text-foreground">{n}</p><p className="text-sm mt-1">{l}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const submit = async (e) => { e.preventDefault(); await api.post("/contact", form); toast.success("Message sent! We'll be in touch soon."); setForm({ name: "", email: "", message: "" }); };
  return (
    <div className="mx-auto max-w-xl px-6 py-16">
      <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] text-center">Get in Touch</p>
      <h1 className="font-display text-4xl text-center mt-2">Contact Us</h1>
      <p className="text-center text-muted-foreground mt-3">Questions, feedback, or wholesale enquiries — we'd love to hear from you.</p>
      <form onSubmit={submit} className="mt-10 space-y-4">
        <Input required placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none h-12" data-testid="contact-name" />
        <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-none h-12" data-testid="contact-email" />
        <Textarea required placeholder="Message" rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="rounded-none" data-testid="contact-message" />
        <Button className="w-full rounded-none h-12 bg-[#D4AF37] text-black font-semibold hover:brightness-110" data-testid="contact-submit">Send Message</Button>
      </form>
    </div>
  );
}

const FAQS = [
  ["How long does shipping take?", "Most orders are dispatched within 1–2 business days and arrive in 3–7 days via USPS, UPS, FedEx or DHL depending on your location."],
  ["Do you ship internationally?", "Yes! We ship to over 50 countries worldwide. Free shipping on orders over $75."],
  ["What is your return policy?", "We offer 30-day hassle-free returns on all items in original condition."],
  ["Which payment methods do you accept?", "We accept all major cards via Stripe, plus Apple Pay and Google Pay at checkout."],
  ["Can I track my order?", "Absolutely. Use your tracking number on our Track Order page for real-time status."],
];

export function FAQ() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <h1 className="font-display text-4xl text-center">Frequently Asked Questions</h1>
      <Accordion type="single" collapsible className="mt-10">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={i} value={`item-${i}`}>
            <AccordionTrigger className="font-display text-lg text-left">{q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function Policy({ title, body }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-display text-4xl">{title}</h1>
      <div className="mt-8 space-y-4 text-muted-foreground leading-relaxed">{body.map((p, i) => <p key={i}>{p}</p>)}</div>
    </div>
  );
}
